import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '../../../../hooks/useUser';
import { useNetworkScope } from '../../../../hooks/useNetworkScope';
import Switcher from './switcher';
import { isInternal } from '../../../../utils';
import axiosInstant from '../../../../api';
import VideoFilters from './videoFilters';
import { ViewToggle } from '../../../../components/ViewToggle/ViewToggle';

const buildQuery = (params: { [key: string]: any }): string => Object.keys(params)
    .map((key) =>
      params[key]
        ? `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
        : ''
    )
    .filter(Boolean)
    .join('&');

interface Props {
  noClearBtn?: boolean;
  isProduct?: boolean;
  isStories?: boolean;
  isStoryVideos?: boolean;
  isExpertVideos?: boolean;
  noTopMargin?: boolean;
  onVideoClick?: () => void;
  onStoryClick?: () => void;
  queryFilters?: any;
  setQueryFilters?: (query: any) => void;
  view?: "grid" | "list";
  onViewChange?: (view: string) => void;
  extraControls?: React.ReactNode;
}

export default function FilterSelector({
  noClearBtn,
  isProduct,
  isStories,
  noTopMargin,
  isStoryVideos,
  isExpertVideos,
  queryFilters,
  setQueryFilters,
  view = "grid",
  onViewChange,
  extraControls,
  ...rest
}: Props) {
  const { role } = useUser();
  const { brandId } = useNetworkScope();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedProduct, setSelectedProduct] = React.useState<string | null>(
    queryFilters?.productId || null
  );
  const [selectedExpert, setSelectedExpert] = React.useState<string | null>(
    queryFilters?.proId || null
  );
  const [selectedBrand, setSelectedBrand] = React.useState<string | null>(
    queryFilters?.brandId || null
  );

  const [products, setProducts] = React.useState<any[]>([]);
  const [brands, setBrands] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);

  const updateSearchParams = React.useCallback(
    (key: string, id?: string) => {
      if (key === "productId") {
        setSelectedProduct(id || null);
      } else if (key === "proId") {
        setSelectedExpert(id || null);
      } else if (key === "brandId") {
        setSelectedBrand(id || null);
      }
      if (setQueryFilters) {
        setQueryFilters({ ...(queryFilters || {}), [key]: id?.toString() });
      } else {
        setSearchParams((params) => {
          params.delete("limit");
          if (!id) params.delete(key);
          else params.set(key, id);
          return params;
        });
      }
    },
    [queryFilters, searchParams]
  );

  React.useEffect(() => {
    setLoading(true);

    const productQuery = buildQuery({
      expert: selectedExpert,
      brand: selectedBrand,
      videos: "true",
    });
    const brandQuery = buildQuery({
      product: selectedProduct,
      expert: selectedExpert,
      videos: "true",
    });

    let productEndpoint = "";
    if (isInternal(role)) {
      productEndpoint = `/products?all=true&${productQuery}`;
    } else {
      productEndpoint = `/brands/${brandId}/products?all=true`;
    }
    axiosInstant
      .get(productEndpoint)
      .then((response) => {
        const output = response.data.rows.map((e: any) => {
          if (!e.desireid || !e.title) return null;
          return {
            id: e.desireid,
            name: e.title,
            image: e.image,
          };
        });
        setProducts(output.filter((e: any) => !!e));
      })
      .catch((err) => console.error("Error fetching products:", err));

    const brandEndpoint = `/brands?all=true&${brandQuery}`;
    axiosInstant
      .get(brandEndpoint)
      .then((response) => {
        const output = response.data.rows.map((res: any) => {
          if (!res.id || !res.name) return null;
          return res;
        });
        setBrands(output.filter((e: any) => !!e));
      })
      .catch((err) => console.error("Error fetching brands:", err))
      .finally(() => setLoading(false));
  }, [selectedProduct, selectedExpert, selectedBrand, role, brandId]);

  const props = { onFilterSelect: updateSearchParams, queryFilters };
  const clearFilterProps = { isProduct, isStoryVideos, isExpertVideos };

  return (
    <div className="flex flex-col xl:flex-row justify-between items-start gap-4">
      {!isStoryVideos && !isExpertVideos && (
        <Switcher isStories={isStories} {...rest} />
      )}
      <div className="flex gap-4 flex-wrap m-0 items-center xl:mt-0 mt-0">
        <VideoFilters
          clearFilterProps={clearFilterProps}
          noClearBtn={noClearBtn}
          isStoryVideos={isStoryVideos}
          isExpertVideos={isExpertVideos}
          isProduct={isProduct}
          products={products}
          videoProps={props}
          isStories={isStories}
          brands={brands}
        />
        {onViewChange && (
          <ViewToggle
            view={view}
            onViewChange={onViewChange}
            gridValue="grid"
            listValue="list"
          />
        )}
        {extraControls}
      </div>
    </div>
  );
}
